<template>
  <v-select
    ref="select"
    :label="hasSelection ? '' : $t('generales.seleccione')"
    :value="coercedValue"
    :items="resolvedItems"
    :item-text="itemText"
    :item-value="itemValue"
    :required="required"
    :rules="rules"
    :disabled="disabled"
    :readonly="readonly"
    :chips="chips"
    :small-chips="smallChips"
    :dense="dense"
    :outlined="outlined"
    :multiple="multiple"
    :clearable="clearable"
    :hide-details="hideDetails"
    :return-object="returnObject"
    @input="$emit('input', $event)"
    @change="$emit('change', $event)"
  />
</template>

<script>
export default {
  name: 'SelectEstaticos',
  props: {
    value: [String, Number, Array, Object, Boolean, null],
    alcance: { type: String, default: '' },
    codigo: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    chips: { type: Boolean, default: false },
    smallChips: { type: Boolean, default: false },
    dense: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    estaticos: { type: Array, default: null },
    outlined: { type: Boolean, default: true },
    hideDetails: { type: String, default: 'auto' },
    // Opción A: trabajamos con ETIQUETAS
    itemText: { type: String, default: 'texto' },
    itemValue: { type: String, default: 'texto' },
    returnObject: { type: Boolean, default: false },
    subalcance: { type: Object, default: null },
    version: { type: Number, default: null },
  },

  data() {
    return {
      items: [],
      db: [],
    }
  },

  computed: {
    rules() {
      if (this.required) {
        return this.multiple
          ? [(v) => (!!v && !!v.length) || this.$t('generales.campoobligatorio')]
          : [(v) => !!v || this.$t('generales.campoobligatorio')]
      }
      return []
    },
    offlineMode() {
      return this.$store.getters['auditorias/offlineMode']
    },

    // Nunca pasamos null en múltiple
    coercedValue() {
      if (this.multiple) {
        return Array.isArray(this.value) ? this.value : []
      } else {
        return Array.isArray(this.value) ? this.value[0] : this.value
      }
    },

    hasSelection() {
      return this.multiple
        ? Array.isArray(this.coercedValue) && this.coercedValue.length > 0
        : !!this.coercedValue
    },

    // Garantiza que haya prop 'texto' (si vinieran strings)
    resolvedItems() {
      const arr = Array.isArray(this.items) ? this.items : []
      return arr.map(it => {
        if (typeof it === 'object') return it
        return { texto: it, valor: it }
      })
    },
  },

  watch: {
    // Cuando llegan items o cambia el value, intentamos “hidratar” por coincidencia laxa
    resolvedItems: {
      handler() { this.hydrateFromValue() },
      immediate: false,
    },
    value() { this.hydrateFromValue() },

    offlineMode(val) {
      if (val) this.init()
    },
    subalcance: {
      handler(val) {
        if (val) this.init()
      },
      deep: true,
    },
  },

  methods: {
    norm(s) {
      return (s == null ? '' : String(s))
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .trim()
        .toLowerCase()
    },

    // Toma el array del back (etiquetas) y lo alinea a los item.texto exactos
    hydrateFromValue() {
      if (!this.multiple) return
      if (!Array.isArray(this.value) || !this.value.length) return
      if (!Array.isArray(this.resolvedItems) || !this.resolvedItems.length) return

      const byNorm = new Map(
        this.resolvedItems.map(it => [this.norm(it[this.itemText]), it[this.itemText]])
      )
      const fixed = this.value
        .map(v => byNorm.get(this.norm(v)))
        .filter(Boolean)

      // Si conseguimos alinear (p.ej., "Ganaderia" -> "Ganadería" exacto), re-emitimos
      if (fixed.length && JSON.stringify(fixed) !== JSON.stringify(this.value)) {
        this.$emit('input', fixed)
        this.$emit('change', fixed)
      }
    },

    setValueByCodigo(codigo) {
      if (typeof codigo !== 'string') return
      const field = this.itemValue
      const found = this.resolvedItems.find((e) => e[field] == codigo)
      if (found) {
        this.$emit('input', found[field])
        this.$emit('change', found[field])
      }
    },

    async getEstaticos() {
      const params = { itemsPerPage: 50, codigo: [this.codigo], version: [this.version] }
      const jxr = await this.$store.dispatch('estaticos/list', params)
      if (jxr) this.db = jxr.data['hydra:member']
    },

    async getEstaticosbySubalcance() {
      const params = {
        itemsPerPage: 50,
        'subalcance.uuid': [this.subalcance?.uuid],
        codigo: [this.codigo],
        version: [this.version],
      }
      const jxr = await this.$store.dispatch('estaticos/list', params)
      if (jxr) this.db = jxr.data['hydra:member']
      if (this.db.length > 0) {
        const map = this.db.reduce((obj, el) => ((obj[el.codigo] = el), obj), {})
        this.items = map[this.codigo]?.valor || []
      } else {
        this.items = []
      }
    },

    async init() {
      let element = null

      if (this.offlineMode) {
        const storage = JSON.parse(localStorage.getItem('estaticos'))
        element = storage && storage[this.codigo]
      } else {
        if (!this.estaticos && this.codigo && !this.subalcance) {
          await this.getEstaticos()
        }

        if (this.subalcance && this.subalcance.uuid) {
          await this.getEstaticosbySubalcance()
        }

        if (this.estaticos) {
          this.db = this.estaticos && this.estaticos[0] ? this.estaticos : []
        }

        if (this.db.length > 0) {
          const map = this.db.reduce((obj, el) => ((obj[el.codigo] = el), obj), {})
          element = map[this.codigo]
        }

        if (element) {
          this.items = element.valor
        }

        // ordenar de forma segura, sin slice()
        const arr = Array.isArray(this.items) ? this.items : []
        this.items = [...arr].sort((a, b) => {
          const ta = (typeof a === 'object' ? a?.texto : a) || ''
          const tb = (typeof b === 'object' ? b?.texto : b) || ''
          return ta > tb ? 1 : (ta < tb ? -1 : 0)
        })
      }
    },
  },

  mounted() {
    this.init().then(() => {
      // Debug SEGURO (sin slice)
      // eslint-disable-next-line no-console
      /*console.log('SelectEstaticos DEBUG:', {
        itemText: this.itemText,
        itemValue: this.itemValue,
        returnObject: this.returnObject,
        multiple: this.multiple,
        valueType: Array.isArray(this.value) ? 'array' : typeof this.value,
        value: this.value,
        itemsCount: Array.isArray(this.items) ? this.items.length : 0,
        sampleItems: (Array.isArray(this.items) ? this.items : []).map(it => (typeof it === 'object' ? it.texto : it)),
      })*/
      // Intento final de hidratación, por si value e items ya están
      this.hydrateFromValue()
    })
  },
}
</script>
